import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { GraduationCap, Play, Award, Clock, Check, Loader2, Sparkles, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import Markdown from "@/components/Markdown";

export function CoursesList() {
  const [courses, setCourses] = useState([]);
  const [my, setMy] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data));
    if (user && user !== false) api.get("/enrollments/my").then((r) => setMy(r.data)).catch(() => {});
  }, [user]);

  const enrolledIds = new Set(my.map((e) => e.course_id));

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      <div className="mb-10 grid md:grid-cols-12 gap-6 items-end">
        <div className="md:col-span-8">
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Vedacosmos Academy</div>
          <h1 className="font-serif text-4xl md:text-6xl tracking-tighter">
            Learn from the <span className="italic gold-gradient">masters.</span>
          </h1>
          <p className="text-[#A0A4AB] mt-4 max-w-lg">
            Bite-sized courses from India's finest jyotishis. Complete lessons, ace quizzes, receive certificates.
          </p>
        </div>
        {my.length > 0 && (
          <div className="md:col-span-4 md:text-right">
            <div className="text-xs text-[#A0A4AB]">Your Progress</div>
            <div className="font-serif text-3xl gold-gradient">{my.length} enrolled</div>
            <div className="text-xs text-emerald-400 mt-1">
              {my.filter((e) => e.certificate_issued).length} certificate{my.filter((e) => e.certificate_issued).length !== 1 ? "s" : ""}
            </div>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {courses.map((c) => (
          <Link
            key={c.id}
            to={`/learn/${c.id}`}
            data-testid={`course-card-${c.id}`}
            className="group rounded-xl overflow-hidden border border-white/10 bg-[#0A0C10] hover:border-[#D4AF37]/40 transition"
          >
            <div className="aspect-[16/10] overflow-hidden relative">
              <img src={c.cover} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt={c.title}/>
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent"/>
              <div className="absolute bottom-3 left-3 flex gap-2">
                <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-[#D4AF37] text-black">{c.level}</span>
                {enrolledIds.has(c.id) && (
                  <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-emerald-500/80 text-white">Enrolled</span>
                )}
              </div>
              <div className="absolute top-3 right-3 text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-black/60 text-white">
                {c.price === 0 ? "FREE" : `₹${c.price}`}
              </div>
            </div>
            <div className="p-6">
              <div className="text-xs text-[#D4AF37]">{c.instructor}</div>
              <div className="font-serif text-xl mt-1 leading-snug">{c.title}</div>
              <p className="text-xs text-[#A0A4AB] mt-3 line-clamp-2">{c.description}</p>
              <div className="flex items-center gap-4 mt-4 text-xs text-[#A0A4AB]">
                <span className="flex items-center gap-1"><Clock size={12}/> {c.duration}</span>
                <span className="flex items-center gap-1"><Play size={12}/> {c.lessons?.length || 0} lessons</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function CourseDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [tab, setTab] = useState("lessons");
  const [activeLesson, setActiveLesson] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/courses/${id}`).then((r) => setCourse(r.data));
  useEffect(() => { load(); }, [id]);

  if (!course) return <div className="p-16 text-center text-[#A0A4AB]">Loading course…</div>;

  const enrolled = course.enrolled;
  const enrollment = course.enrollment;
  const completed = new Set(enrollment?.completed_lessons || []);
  const progress = enrollment?.progress || 0;

  const enroll = async () => {
    if (!user || user === false) { nav("/login"); return; }
    setBusy(true);
    try {
      await api.post("/courses/enroll", { course_id: id });
      toast.success("Enrolled successfully!");
      await load();
    } catch { toast.error("Enrollment failed"); }
    setBusy(false);
  };

  const completeLesson = async (idx) => {
    if (!enrolled) { toast("Please enroll first"); return; }
    try {
      const { data } = await api.post("/courses/lesson/complete", { course_id: id, lesson_index: idx });
      toast.success(`Progress: ${data.progress}%`);
      await load();
    } catch (e) { toast.error("Failed"); }
  };

  const submitQuiz = async () => {
    const answers = course.quiz.map((_, i) => quizAnswers[i] ?? -1);
    setBusy(true);
    try {
      const { data } = await api.post("/courses/quiz/attempt", { course_id: id, answers });
      setQuizResult(data);
      await load();
      if (data.passed) toast.success(`🏆 Passed with ${data.score}%! Certificate issued.`);
      else toast(`Scored ${data.score}%. 60% needed to pass.`);
    } catch { toast.error("Quiz submission failed"); }
    setBusy(false);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      {/* Header */}
      <div className="rounded-2xl overflow-hidden border border-white/10 mb-8">
        <div className="relative aspect-[3/1] overflow-hidden">
          <img src={course.cover} className="w-full h-full object-cover" alt=""/>
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent"/>
          <div className="absolute bottom-0 left-0 right-0 p-8">
            <div className="text-xs uppercase tracking-widest text-[#D4AF37] mb-2">{course.instructor}</div>
            <h1 className="font-serif text-4xl md:text-5xl">{course.title}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-[#A0A4AB]">
              <span className="flex items-center gap-1"><Clock size={14}/> {course.duration}</span>
              <span className="flex items-center gap-1"><Play size={14}/> {course.lessons.length} lessons</span>
              <span className="px-2 py-1 rounded bg-[#D4AF37] text-black text-[10px] uppercase tracking-widest">{course.level}</span>
              {enrollment?.certificate_issued && (
                <span className="px-2 py-1 rounded bg-emerald-500/20 border border-emerald-500/50 text-emerald-400 text-[10px] uppercase tracking-widest inline-flex items-center gap-1">
                  <Award size={11}/> Certified
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="p-6 flex items-center justify-between gap-4 flex-wrap bg-[#0A0C10]">
          <div className="flex-1 min-w-[200px]">
            <p className="text-sm text-[#A0A4AB]">{course.description}</p>
            {enrolled && (
              <div className="mt-3">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-[#A0A4AB]">Progress</span>
                  <span className="text-[#D4AF37]">{progress}%</span>
                </div>
                <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full bg-[#D4AF37] transition-all" style={{ width: `${progress}%` }}/>
                </div>
              </div>
            )}
          </div>
          {!enrolled ? (
            <button data-testid="enroll-btn" onClick={enroll} disabled={busy} className="px-6 py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#E5C865] transition disabled:opacity-60">
              {busy ? "Enrolling…" : "Enroll Free"}
            </button>
          ) : (
            <div className="text-emerald-400 text-sm inline-flex items-center gap-1">
              <Check size={14}/> You're enrolled
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 mb-6">
        {[{k: "lessons", l: "Lessons"}, {k: "quiz", l: "Quiz"}, {k: "certificate", l: "Certificate"}].map((t) => (
          <button
            key={t.k}
            data-testid={`course-tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`px-5 py-3 text-sm border-b-2 transition ${
              tab === t.k ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-[#A0A4AB] hover:text-white"
            }`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {/* LESSONS */}
      {tab === "lessons" && (
        <div className="grid md:grid-cols-12 gap-4">
          <div className="md:col-span-4 space-y-2">
            {course.lessons.map((l, i) => (
              <button
                key={`${l.title}-${i}`}
                data-testid={`lesson-btn-${i}`}
                onClick={() => setActiveLesson(i)}
                className={`w-full text-left p-4 rounded-lg border transition ${
                  activeLesson === i ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-white/10 hover:border-white/25"
                } ${completed.has(i) ? "opacity-90" : ""}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs ${
                    completed.has(i) ? "bg-emerald-500/20 border border-emerald-500 text-emerald-400"
                                     : "bg-[#12151C] border border-white/10 text-[#A0A4AB]"
                  }`}>
                    {completed.has(i) ? <Check size={14}/> : i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#EAE6DF] truncate">{l.title}</div>
                    <div className="text-xs text-[#A0A4AB]">{l.duration}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
          <div className="md:col-span-8 p-8 rounded-xl bg-[#0A0C10] border border-white/10 min-h-[400px]">
            <div className="text-xs uppercase tracking-widest text-[#D4AF37]">Lesson {activeLesson + 1}</div>
            <h3 className="font-serif text-2xl mt-2">{course.lessons[activeLesson].title}</h3>
            <div className="text-xs text-[#A0A4AB] mt-1 mb-6">{course.lessons[activeLesson].duration} · {course.instructor}</div>
            <p className="text-[#EAE6DF] leading-relaxed">{course.lessons[activeLesson].content}</p>
            {enrolled && !completed.has(activeLesson) && (
              <button
                data-testid="lesson-complete-btn"
                onClick={() => completeLesson(activeLesson)}
                className="mt-8 px-5 py-2 rounded-full bg-[#D4AF37] text-black text-sm hover:bg-[#E5C865] transition"
              >
                Mark as complete
              </button>
            )}
          </div>
        </div>
      )}

      {/* QUIZ */}
      {tab === "quiz" && (
        <div className="p-8 rounded-xl bg-[#0A0C10] border border-white/10">
          {!enrolled ? (
            <div className="text-center text-[#A0A4AB]">Enroll to attempt the quiz.</div>
          ) : quizResult ? (
            <div className="text-center py-8">
              <div className={`font-serif text-6xl ${quizResult.passed ? "gold-gradient" : "text-[#FF5A36]"}`}>
                {quizResult.score}%
              </div>
              <div className="text-lg mt-2">
                {quizResult.passed ? "🎉 You passed!" : "Not yet — try again."}
              </div>
              <div className="text-xs text-[#A0A4AB] mt-1">{quizResult.correct} / {quizResult.total} correct</div>
              <button onClick={() => { setQuizResult(null); setQuizAnswers({}); }} className="mt-6 px-5 py-2 rounded-full border border-white/20 text-sm hover:border-[#D4AF37] transition">
                Retake Quiz
              </button>
            </div>
          ) : (
            <>
              <div className="font-serif text-2xl mb-6">Final Quiz</div>
              <div className="space-y-6">
                {course.quiz.map((q, i) => (
                  <div key={`q-${i}-${q.q.slice(0, 20)}`}>
                    <div className="text-sm mb-3">
                      <span className="text-[#D4AF37] mr-2">Q{i + 1}.</span> {q.q}
                    </div>
                    <div className="grid gap-2">
                      {q.options.map((opt, oi) => (
                        <button
                          key={`${i}-${opt}`}
                          data-testid={`quiz-q${i}-opt${oi}`}
                          onClick={() => setQuizAnswers({ ...quizAnswers, [i]: oi })}
                          className={`p-3 rounded-lg border text-left text-sm transition ${
                            quizAnswers[i] === oi ? "border-[#D4AF37] bg-[#D4AF37]/10"
                                                  : "border-white/10 hover:border-white/25"
                          }`}
                        >
                          {String.fromCharCode(65 + oi)}. {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <button
                data-testid="quiz-submit-btn"
                onClick={submitQuiz}
                disabled={busy || Object.keys(quizAnswers).length !== course.quiz.length}
                className="mt-8 px-6 py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#E5C865] transition disabled:opacity-50"
              >
                {busy ? "Submitting…" : "Submit Quiz"}
              </button>
            </>
          )}
        </div>
      )}

      {/* CERTIFICATE */}
      {tab === "certificate" && (
        <div className="p-8">
          {enrollment?.certificate_issued ? (
            <div className="mx-auto max-w-2xl p-12 rounded-2xl bg-gradient-to-br from-[#12151C] to-[#0A0C10] border-2 border-[#D4AF37]">
              <div className="text-center">
                <div className="w-20 h-20 mx-auto rounded-full border-2 border-[#D4AF37] flex items-center justify-center">
                  <Award className="text-[#D4AF37]" size={32}/>
                </div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mt-6">Vedacosmos Academy</div>
                <div className="font-serif text-2xl mt-2">Certificate of Completion</div>
                <div className="text-sm text-[#A0A4AB] mt-6">Awarded to</div>
                <div className="font-serif text-4xl gold-gradient mt-1">{user?.name}</div>
                <div className="text-sm text-[#A0A4AB] mt-6">for successful completion of</div>
                <div className="font-serif text-xl mt-1">{course.title}</div>
                <div className="text-xs text-[#A0A4AB] mt-2">by {course.instructor}</div>
                <div className="mt-8 pt-6 border-t border-white/10 flex justify-between text-xs">
                  <div>
                    <div className="text-[#A0A4AB]">Certificate No.</div>
                    <div className="text-[#D4AF37] mt-1 font-mono">{enrollment.certificate_id}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[#A0A4AB]">Score</div>
                    <div className="text-[#D4AF37] mt-1">{enrollment.quiz_score}%</div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-[#A0A4AB]">
              Complete all lessons & pass the quiz (60%+) to earn your certificate.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
